import {
  ReactElement,
  SyntheticEvent,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  AppBar,
} from '@mui/material';
import { TabContext, TabList } from '@mui/lab';
import { useNavigate } from 'react-router-dom';

import {
  NewFeatureBagde,
  TrackingOrderPageInfo,
  AccessFeature,
} from '~components/index';
import { useRequest } from '~hooks/index';
import { getFeedbackRouteItem } from '~services/route';
import { RouteItemFeedback } from '~services/route/types';
import { Nullable } from '~globals/types';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { isRouteStopOrderFinalized } from '~utils/route';
import { convertApiTransactionRouteItemInItemExtended } from '~utils/order';
import { openUrlInNewTab } from '~utils/commons';
import { replaceParamsPath } from '~utils/router';
import { PATHS } from '~constants/paths';

import {
  DialogFeedbackStopProps,
  DialogFeedbackStopActiveTab,
  DialogFeedbackStopTabsList,
  DialogFeedbackStopTabsListPanel,
} from './types';
import DialogFeedbackStopProvider from './DialogFeedbackStopContext';
import {
  DialogFeebackStopData,
  DialogFeebackStopFeedback,
  DialogFeebackStopArticles,
  DialogFeebackStopOrders,
  DialogFeebackStopHistory,
} from './Tabs';
import { DialogDetailsOrderBadgeState } from '../DialogDetailsOrder';
import {
  DialogFeedbackStopContainer,
  DialogFeedbackStopLoadingContainer,
  DialogFeedbackStopHeader,
  DialogFeedbackStopHeaderTitle,
  DialogFeedbackStopContentContainer,
  DialogFeedbackStopTab,
  DialogFeedbackStopTabsPanelContainer,
  DialogFeedbackStopTabPanel,
} from './styles';

const DialogFeedbackStop = ({
  open,
  onClose,
  stopId,
  orderId,
  enabledFeebackPanel = true,
  mapRef,
  enabledChipTitle = true,
  currentTab,
  showInfoInNewWindow = false,
}: DialogFeedbackStopProps): ReactElement => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<DialogFeedbackStopActiveTab>(
    currentTab ?? 'data',
  );

  const [feedbackData, setFeebackData] =
    useState<Nullable<RouteItemFeedback>>(null);

  const [isWarehouse, setIsWarehouse] = useState(false);
  const [isCollectItem, setIsCollectItem] = useState(false);

  const [, loadingFeeback, , reloadRequest] = useRequest({
    request: getFeedbackRouteItem,
    payload: stopId,
    withPostSuccess: (response) => {
      const feedbackResponse = response.data?.data as RouteItemFeedback;

      if (
        isRouteStopOrderFinalized(
          feedbackResponse.routeItem.routeItemStateTypeId,
        ) &&
        !currentTab
      ) {
        setActiveTab('feedback');
      }

      setFeebackData(feedbackResponse);
      setIsWarehouse(Boolean(feedbackResponse?.routeItem.warehouseId));
      setIsCollectItem(
        Boolean(feedbackResponse?.routeItem.item?.isCollectItem),
      );
    },
  });

  const reloadData = useCallback(() => {
    reloadRequest(stopId);
  }, [reloadRequest, stopId]);

  const tabsList = useMemo(
    (): DialogFeedbackStopTabsList[] => [
      {
        id: 'data',
        label: 'Datos de parada',
        enabled: true,
        newActiveForVersion: '',
      },
      {
        id: 'feedback',
        label: 'Detalles de visita',
        enabled: enabledFeebackPanel,
        newActiveForVersion: '',
      },
      {
        id: 'articles',
        label: 'Artículos / Tareas',
        enabled: !isWarehouse && !isCollectItem,
        newActiveForVersion: '1.14.0',
      },
      {
        id: 'orders',
        label: 'Pedidos',
        enabled: !isWarehouse && isCollectItem,
        newActiveForVersion: '',
      },
      {
        id: 'history',
        label: 'Historial',
        enabled: !isWarehouse,
        newActiveForVersion: '',
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitPaid,
        },
      },
    ],
    [enabledFeebackPanel, isWarehouse, isCollectItem],
  );

  const tabsListPanels = useMemo(
    (): DialogFeedbackStopTabsListPanel[] => [
      {
        id: tabsList[0].id,
        panel: DialogFeebackStopData,
        enabled: true,
      },
      {
        id: tabsList[1].id,
        panel: DialogFeebackStopFeedback,
        enabled: enabledFeebackPanel,
      },
      {
        id: tabsList[2].id,
        panel: DialogFeebackStopArticles,
        enabled: !isWarehouse && !isCollectItem,
      },
      {
        id: tabsList[3].id,
        panel: DialogFeebackStopOrders,
        enabled: !isWarehouse && isCollectItem,
      },
      {
        id: tabsList[4].id,
        panel: DialogFeebackStopHistory,
        enabled: !isWarehouse,
      },
    ],
    [tabsList, enabledFeebackPanel, isWarehouse, isCollectItem],
  );

  const handleChangeActiveTab = useCallback(
    (_e: SyntheticEvent, newValue: DialogFeedbackStopActiveTab) => {
      setActiveTab(newValue);
    },
    [],
  );

  const onHandleClickShowInfo = useCallback(() => {
    const urlShowInfoOrder = replaceParamsPath(PATHS.ORDERS.SHOW, {
      ':orderId': orderId,
    });

    if (showInfoInNewWindow) {
      openUrlInNewTab(urlShowInfoOrder);
    } else {
      navigate(urlShowInfoOrder);
    }

    onClose();
  }, [orderId, showInfoInNewWindow, navigate, onClose]);

  const routeItemStateTypeId = useMemo(
    () => feedbackData?.routeItem.routeItemStateTypeId ?? null,
    [feedbackData],
  );

  return (
    <DialogFeedbackStopProvider
      value={{
        data: feedbackData,
        routeItemStateTypeId,
        reloadData,
        mapRef,
        onClose,
        orderId,
      }}
    >
      <DialogFeedbackStopContainer open={open} maxWidth={false}>
        {loadingFeeback && (
          <DialogFeedbackStopLoadingContainer>
            <CircularProgress color="primary" disableShrink size={80} />
          </DialogFeedbackStopLoadingContainer>
        )}

        {!loadingFeeback && (
          <>
            <DialogFeedbackStopHeader>
              <DialogFeedbackStopHeaderTitle>
                <Typography component="h2" variant="h6">
                  {feedbackData?.routeItem.warehouse?.title ??
                    feedbackData?.routeItem.item?.title}
                </Typography>

                {enabledChipTitle && feedbackData?.routeItem && (
                  <DialogDetailsOrderBadgeState
                    itemData={convertApiTransactionRouteItemInItemExtended(
                      feedbackData.routeItem,
                      null,
                    )}
                  />
                )}
              </DialogFeedbackStopHeaderTitle>

              {feedbackData?.routeItem?.item?.trackingCode && (
                <TrackingOrderPageInfo
                  trackingCode={feedbackData.routeItem.item.trackingCode}
                />
              )}
            </DialogFeedbackStopHeader>

            <DialogFeedbackStopContentContainer>
              <TabContext value={activeTab}>
                <AppBar position="sticky" color="tertiary" elevation={0}>
                  <TabList
                    onChange={handleChangeActiveTab}
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    {tabsList.map(
                      ({
                        label,
                        id,
                        enabled,
                        newActiveForVersion,
                        validSubscriptionFeature,
                      }) =>
                        enabled && (
                          <DialogFeedbackStopTab
                            key={`dialog-feeback-stop-tab-${id}`}
                            label={label}
                            value={id}
                            icon={
                              <div>
                                {newActiveForVersion && (
                                  <NewFeatureBagde
                                    activeForVersion={newActiveForVersion}
                                  />
                                )}

                                {validSubscriptionFeature && (
                                  <AccessFeature.NextPlanChip
                                    {...validSubscriptionFeature}
                                  />
                                )}
                              </div>
                            }
                            iconPosition="end"
                          />
                        ),
                    )}
                  </TabList>
                </AppBar>

                <DialogFeedbackStopTabsPanelContainer>
                  {tabsListPanels.map(
                    ({ id, panel: Panel, enabled }) =>
                      enabled && (
                        <DialogFeedbackStopTabPanel
                          key={`dialog-feeback-stop-panel-${id}`}
                          value={id}
                        >
                          <Panel />
                        </DialogFeedbackStopTabPanel>
                      ),
                  )}
                </DialogFeedbackStopTabsPanelContainer>
              </TabContext>
            </DialogFeedbackStopContentContainer>

            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Button color="secondary" variant="outlined" onClick={onClose}>
                Cerrar
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={onHandleClickShowInfo}
              >
                Ver información completa
              </Button>
            </DialogActions>
          </>
        )}
      </DialogFeedbackStopContainer>
    </DialogFeedbackStopProvider>
  );
};

export default DialogFeedbackStop;
